import * as THREE from 'three/webgpu';
import { PointsMaterialFactory } from '../../../../material/PointsMaterialFactory';
import type { SketchGroup } from '../../../../mesh/sketch/SketchGroup';
import {
	SketchPointKind,
} from '../../../../mesh/sketch/ISketchObject';
import {
	SketchArcObject,
	type SketchArcDefinition,
} from '../../../../mesh/sketch/SketchArcObject';
import { SketchPointObject } from '../../../../mesh/sketch/SketchPointObject';
import type { ProjectionResult } from '../../model/ProjectionResult';
import type {
	ArcReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/ArcReferenceBuilder';

export type {
	ArcReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/ArcReferenceBuilder';

/**
 * 圆弧引用构建器。
 * 说明：
 * 1. 使用投影后的圆心、半径和起终角创建正式圆弧对象；
 * 2. 起终点与圆心控制点统一使用控制点材质，避免默认异步贴图路径；
 * 3. 不处理关系恢复，仅保证对象拓扑和几何完整。
 */
export class ArcReferenceBuilder {
	/**
	 * 构建草图圆弧对象。
	 *
	 * @param input 构建输入。
	 * @returns 已完成几何初始化的圆弧对象。
	 */
	public build(input: ArcReferenceBuilderInput): SketchArcObject {
		const definition: SketchArcDefinition = {
			centerWorld: new THREE.Vector3(...input.geometryPayload.centerWorld),
			radius: input.geometryPayload.radius,
			startAngle: input.geometryPayload.startAngle,
			endAngle: input.geometryPayload.endAngle,
			clockwise: input.geometryPayload.clockwise,
		};
		const startPoint = createControlPoint(
			resolveCircularWorldPoint(definition.centerWorld, definition.radius, definition.startAngle, input.targetSketch),
			input.targetSketch,
		);
		const endPoint = createControlPoint(
			resolveCircularWorldPoint(definition.centerWorld, definition.radius, definition.endAngle, input.targetSketch),
			input.targetSketch,
		);
		const centerPoint = createControlPoint(definition.centerWorld, input.targetSketch);
		const arc = new SketchArcObject({
			referencePlane: input.targetSketch.referencePlane,
			definition,
			startPoint,
			endPoint,
			centerPoint,
		});

		arc.updateArc();
		return arc;
	}
}

/**
 * 计算圆参数点在目标草图平面上的世界坐标。
 *
 * @param centerWorld 圆心世界坐标。
 * @param radius 半径。
 * @param angle 参数角。
 * @param targetSketch 目标草图。
 * @returns 世界坐标点。
 */
function resolveCircularWorldPoint(
	centerWorld: THREE.Vector3,
	radius: number,
	angle: number,
	targetSketch: SketchGroup,
): THREE.Vector3 {
	const localCenter = targetSketch.referencePlane.worldToLocal(centerWorld);
	return targetSketch.referencePlane.localToWorld(
		localCenter.x + Math.cos(angle) * radius,
		localCenter.y + Math.sin(angle) * radius,
	);
}

/**
 * 创建引用链路使用的控制点。
 *
 * @param position 世界坐标。
 * @param targetSketch 目标草图。
 * @returns 控制点对象。
 */
function createControlPoint(
	position: THREE.Vector3,
	targetSketch: SketchGroup,
): SketchPointObject {
	return new SketchPointObject({
		position,
		pointKind: SketchPointKind.ControlPoint,
		material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
		referencePlane: targetSketch.referencePlane,
	});
}
